import { useEffect, useState } from 'react';
import { Alert, AlertColor, Box, Button, Card, CardContent, CircularProgress, FormControl, Grid, Icon, InputLabel, ListItemText, MenuItem, OutlinedInput, Paper, Select, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { useVForm } from 'shared/forms';

import { FerramentasDeDetalhe, InputError, ConfirmacaoModal } from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { t } from 'i18next';
import { GrupoTesteService } from 'shared/services/grupoTeste/grupoTesteService';
import { ICreateGrupoTeste, ICreateGrupoTesteDestinatario, InicitalCreateGrupoTeste, InicitalCreateGrupoTesteDestinatario, listKeyValueTipoGrupoTeste } from 'shared/types';
import { useHasPermissionAcao } from 'shared/hooks';
import { useSelectedOrganization } from 'shared/store/organization';
import { CanaisService } from 'shared/services/canais/canaisService';
import { IKeyValue, listagemCanalToIKeyValue } from 'shared/utils';


const validationSchema = yup.object({
  NomeDestinatario: yup.string().required(),
  DescricaoDestinatario: yup.string().required(),
});

export const DetalheDeGrupoTesteDestinatario: React.FC = () => {
  const {idCanal} = useParams<'idCanal'>();
  const {idGrupoTeste} = useParams<'idGrupoTeste'>();
  const { id = 'novo' } = useParams<'id'>();
  const org = useSelectedOrganization();
  const navigate = useNavigate();

  const hasPermitionCreate = useHasPermissionAcao('grupoteste','CRE');
  const hasPermitionDelete = useHasPermissionAcao('grupoteste','DEL');
  const hasPermitionUpdate = useHasPermissionAcao('grupoteste','UPD');

  
  const [alert, setAlert] = useState<null | { text: string; type: AlertColor }>(null);
  const { formRef, saveAndClose, isSaveAndClose } = useVForm();

  const { register, handleSubmit, reset, formState: { errors }, } = useForm({
    resolver: yupResolver(validationSchema),
    reValidateMode: 'onBlur'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openConfirmacao, setOpenConfirmacao] = useState(false);
  const [titleConfirmacao, setTitleConfirmacao] = useState('');
  const [messageConfirmacao, setMessageConfirmacao] = useState('');

  useEffect(() => {
    setAlert(null);
    if (id !== 'novo') {
      setIsLoading(true);

      GrupoTesteService.getDestinatarioById(Number(org?.CodigoOrganizacao), Number(idCanal), Number(idGrupoTeste), Number(id))
        .then((result) => {
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
            navigate(`/grupoteste/detalhe/${idCanal}/${idGrupoTeste}`);
          } else {
            delete result.ROW_NUM;
            reset(result);
          }
          setIsLoading(false);
        });
    }
    else {
      reset(InicitalCreateGrupoTesteDestinatario);
    }

  }, [id]);

  const handleDelete = () => {
    setTitleConfirmacao(t('components.actions.apagar'));
    setMessageConfirmacao(t('messages.grupoTesteDestinatario.exclusion') + id + '?');
    setOpenConfirmacao(true);
    //console.log('handleDelete');
  };

  const confirmDelete = () => {
    setAlert(null);
    GrupoTesteService.deleteDestinatarioById(Number(org?.CodigoOrganizacao), Number(idCanal), Number(idGrupoTeste), Number(id))
      .then(result => {
        if (result instanceof Error) {
          setAlert({
            text: result.message,
            type: 'error',
          });
          setOpenConfirmacao(false);
        } else {
          setAlert({
            text: 'Registro apagado com sucesso!',
            type: 'success',
          });
          navigate(`/grupoteste/detalhe/${idCanal}/${idGrupoTeste}`);
        }
      })
      .catch( reason => {
        setAlert({
          text: reason,
          type: 'error',
        });
        setOpenConfirmacao(false);
      });
  };

  const onSubmit = async (data: ICreateGrupoTesteDestinatario) => {
    setAlert(null);
    setIsSubmitting(true);
    if (id === 'novo') {
      GrupoTesteService
        .createDestinatario(Number(org?.CodigoOrganizacao), Number(idCanal), Number(idGrupoTeste), data)
        .then((result) => { 
          setIsLoading(false);
          if (result instanceof Error) {  
            setAlert({
              text: result.message,
              type: 'error',
            });
          } else {
            console.log('isSaveAndClose',isSaveAndClose());
            if (isSaveAndClose()) {
              navigate(`/grupoteste/detalhe/${idCanal}/${idGrupoTeste}/Destinatario`);
            } else {
              navigate(`/grupoteste/detalhe/${idCanal}/${idGrupoTeste}/Destinatario/${result}`);
            }
          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });

    } else {
      GrupoTesteService
        .updateDestinatarioById(Number(org?.CodigoOrganizacao), Number(idCanal), Number(idGrupoTeste),Number(id), data)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
          } else {
            if (isSaveAndClose()) {
              navigate(`/grupoteste/detalhe/${idCanal}/${idGrupoTeste}/Destinatario`);
            }
            setAlert({
              text: t('messages.success.alterado'), 
              type: 'success',
            });

          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });

    }

  };



  return (
    <LayoutBaseDePagina
      titulo={id === 'novo' ? 'Novo Destinatário Grupo Teste / Seed List' : hasPermitionUpdate?'Alterar Destinatário Grupo Teste / Seed List':'Consultar Destinatário Grupo Teste / Seed List'}
      
      barraDeFerramentas={!(isLoading || isSubmitting) && (
        <FerramentasDeDetalhe
          idForm='formDetalheDegrupoTesteDestinatario'
          mostrarBotaoSalvarEFechar={hasPermitionCreate && id == 'novo'}
          mostrarBotaoSalvar={false}
          mostrarBotaoNovo={false}
          mostrarBotaoApagar={false}
          aoClicarEmVoltar={() => navigate(`/grupoteste/detalhe/${idCanal}/${idGrupoTeste}/destinatario`)}
          aoClicarEmSalvarEFechar={saveAndClose}
          
        />)
      }
    >
      <Box ref={formRef} id='formDetalheDegrupoTesteDestinatario' component="form" display="flex" autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined" width='100%' >

          <Card >
            <CardContent sx={{ padding: 0 }} >
              <div>
                {alert ? (
                  <>
                    <Grid item xs={12} paddingX={2}>
                      <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ marginTop: '1rem', marginBottom: '0' }}>
                        {alert.text}
                      </Alert>
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12} component={Typography} paddingX={2}>

                  </Grid>
                )}
                <Grid container  >
                  <Grid item xs={12} paddingX={2}>
                    <TextField
                      error={errors?.NomeDestinatario?.type ? true : false}
                      fullWidth
                      id='NomeDestinatario'
                      type='text'
                      label={`${t('pages.grupoTesteDestinatario.nome')} *`}
                      disabled={isSubmitting || isLoading || (!hasPermitionUpdate && id !== 'novo')}
                      {...register('NomeDestinatario')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.NomeDestinatario?.type && <InputError type={errors.NomeDestinatario.type} field='NomeDestinatario' />}
                  </Grid>
                  <Grid item xs={12} paddingX={2}>
                    <TextField
                      error={errors?.DescricaoDestinatario?.type ? true : false}
                      fullWidth
                      id='DescricaoDestinatario'
                      type='text'
                      label={`${t('pages.grupoTesteDestinatario.descricao')} *`}
                      disabled={isSubmitting || isLoading || (!hasPermitionUpdate && id !== 'novo')}
                      {...register('DescricaoDestinatario')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.DescricaoDestinatario?.type && <InputError type={errors.DescricaoDestinatario.type} field='DescricaoDestinatario' />}
                  </Grid>
                </Grid>
              </div>
            </CardContent>
          </Card>
        </Box>
        <ConfirmacaoModal
          open={openConfirmacao}
          title={titleConfirmacao}
          message={messageConfirmacao}
          onClose={() => setOpenConfirmacao(false)}
          onConfirm={() => confirmDelete()}
        />
      </Box>
      {(isLoading || isSubmitting) && (<Box sx={{
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <CircularProgress />
      </Box>)}

    </LayoutBaseDePagina >
  );
};
